import { PortalShell } from "@/components/PortalShell";
import { RequireSession, SessionProvider } from "@/lib/session";
import { ToastProvider } from "@/lib/toast";
import { AccountRoute } from "@/routes/account";
import { ClaimRoute } from "@/routes/claim";
import { LicenseDetailRoute } from "@/routes/license-detail";
import { NotFoundRoute } from "@/routes/not-found";
import { OverviewRoute } from "@/routes/overview";
import { SignInRoute } from "@/routes/signin";
import { SupportRoute } from "@/routes/support";
import { SupportNewRoute } from "@/routes/support-new";
import { TicketDetailRoute } from "@/routes/ticket-detail";
import { VerifyRoute } from "@/routes/verify";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createBrowserRouter } from "react-router";
import "@/styles/globals.css";

/**
 * Every screen is inside the shell, and every screen except the two sign-in
 * ones is inside `RequireSession`.
 *
 * The gate wraps each route element rather than sitting on a parent route with
 * its own layout, because `/signin` and `/signin/verify` need the same header
 * and footer as everything else — a sign-in page that loses the brand and the
 * theme toggle reads as a different site, which is exactly the wrong feeling on
 * the page where somebody types their email address.
 *
 * The providers are here rather than inside `PortalShell` so that a route
 * error, which unmounts the shell, does not take the toast viewport and the
 * session with it.
 */
const router = createBrowserRouter([
  {
    element: <PortalShell />,
    children: [
      {
        index: true,
        element: (
          <RequireSession>
            <OverviewRoute />
          </RequireSession>
        ),
      },
      { path: "signin", element: <SignInRoute /> },
      { path: "signin/verify", element: <VerifyRoute /> },
      {
        path: "licenses/:licenseId",
        element: (
          <RequireSession>
            <LicenseDetailRoute />
          </RequireSession>
        ),
      },
      {
        path: "claim",
        element: (
          <RequireSession>
            <ClaimRoute />
          </RequireSession>
        ),
      },
      {
        path: "support",
        element: (
          <RequireSession>
            <SupportRoute />
          </RequireSession>
        ),
      },
      // Ahead of `support/:ticketId`, because `new` is the one path segment
      // under /support that is a verb rather than an id — the same ordering
      // hazard `/api/licenses/claim` has in the Worker.
      {
        path: "support/new",
        element: (
          <RequireSession>
            <SupportNewRoute />
          </RequireSession>
        ),
      },
      {
        path: "support/:ticketId",
        element: (
          <RequireSession>
            <TicketDetailRoute />
          </RequireSession>
        ),
      },
      {
        path: "account",
        element: (
          <RequireSession>
            <AccountRoute />
          </RequireSession>
        ),
      },
      { path: "*", element: <NotFoundRoute /> },
    ],
  },
]);

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Missing #root element");

createRoot(rootEl).render(
  <StrictMode>
    <ToastProvider>
      <SessionProvider>
        <RouterProvider router={router} />
      </SessionProvider>
    </ToastProvider>
  </StrictMode>,
);
